import React from "react";
import { Box, Typography, Button, IconButton } from "@mui/material";
import { MoreVert } from "@mui/icons-material";
import { LoadingButton } from "@mui/lab";
import {
  Brand,
  Batch,
  BatchStats,
  BatchAdvancedStats,
  SheetData,
  User,
} from "../../../pages/playground/types";

export interface JobListHeaderProps {
  brand?: Brand;
  batch?: Batch;
  stats: BatchStats | null;
  advancedStats: BatchAdvancedStats | null;
  sheet: SheetData | null;
  user?: User;
  isPolling: boolean;
  canPreProcess: boolean;
  isCompleted: boolean;
  showBulkActions: boolean;
  onToggleBulkActions: () => void;
  onStartPreprocess: () => Promise<void>;
  onOpenMenu: (event: React.MouseEvent<HTMLElement>) => void;
  showUploadImagesButton: boolean;
  showClientImageUpload: boolean;
  setShowClientImageUpload: (value: boolean) => void;
  showKeywordSearch: boolean;
  onToggleKeywordSearch: () => void;
}

/**
 * Display title, SKU counts, and primary action buttons
 */
export const JobListHeader: React.FC<JobListHeaderProps> = ({
  stats,
  advancedStats,
  sheet,
  user,
  isPolling,
  canPreProcess,
  isCompleted,
  showBulkActions,
  onToggleBulkActions,
  onStartPreprocess,
  onOpenMenu,
  showUploadImagesButton,
  showClientImageUpload,
  setShowClientImageUpload,
  showKeywordSearch,
  onToggleKeywordSearch,
}) => {
  const totalItems = stats?.total_jobs || 0;

  return (
    <Box
      sx={{
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
      }}
    >
      <Box sx={{ display: "flex", gap: 1, flexDirection: "column" }}>
        <Typography variant="h4">Jobs</Typography>
        <Typography
          variant="body1"
          sx={{
            display: "flex",
            gap: 1,
            alignItems: "center",
            fontSize: "0.8rem",
          }}
        >
          {user?.isCopywriter ? (
            <>
              <span>{stats?.total_jobs} SKUs assigned to you</span>
            </>
          ) : (
            <>
              {sheet ? (
                <span>{sheet?.items?.length || 0} SKU in Master File</span>
              ) : (
                "Loading..."
              )}

              {totalItems ? (
                <>
                  <span>&bull;</span>
                  <span>{totalItems} loaded</span>
                </>
              ) : (
                ""
              )}
            </>
          )}
        </Typography>
      </Box>
      <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
        {/* Primary Actions - Always visible */}
        {canPreProcess && (
          <Button
            variant="contained"
            size="small"
            color="primary"
            disabled={isPolling}
            onClick={onStartPreprocess}
          >
            Restart pre-processing
          </Button>
        )}

        {showUploadImagesButton && (
          <Button
            variant="contained"
            size="small"
            color="secondary"
            onClick={() => {
              setShowClientImageUpload(!showClientImageUpload);
            }}
          >
            {showClientImageUpload ? "Hide Upload Images" : "Upload images"}
          </Button>
        )}

        {(user?.isManager || user?.isAdmin) && isCompleted && (
          <Button
            variant="contained"
            color="info"
            size="small"
            onClick={onToggleBulkActions}
          >
            {showBulkActions ? "Exit Bulk" : "Bulk Actions"}
          </Button>
        )}

        {user?.isCopywriter && isCompleted && (
          <Button
            variant="contained"
            color="secondary"
            size="small"
            onClick={onToggleKeywordSearch}
          >
            {showKeywordSearch ? "Exit Keywords Search" : "Keywords Search"}
          </Button>
        )}

        {!user?.isCopywriter && showKeywordSearch && isCompleted && (
          <Button
            variant="contained"
            color="secondary"
            size="small"
            onClick={onToggleKeywordSearch}
          >
            Exit Keywords Search
          </Button>
        )}

        {/* Secondary Actions Menu */}
        {(sheet ||
          user?.isAdmin ||
          (advancedStats && isCompleted) ||
          (isCompleted && stats)) && (
          <IconButton onClick={onOpenMenu} size="small" sx={{ ml: 1 }}>
            <MoreVert />
          </IconButton>
        )}

        {(isPolling || !sheet) && <LoadingButton loading />}
      </Box>
    </Box>
  );
};
